import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck, Factory, Palette, BadgeDollarSign, Globe2, ArrowUpRight,
  Package, PencilRuler, Boxes, Tag,
} from "lucide-react";
import { api } from "../lib/api";
import { IMAGES, pimg, formatCategoryName } from "../lib/site";
import { useSettings } from "../context/SettingsContext";
import { MaskedLines, FadeIn, Reveal, EASE } from "../components/motion/Reveal";
import SectionHeading from "../components/SectionHeading";
import EditorialMarquee from "../components/EditorialMarquee";
import ProductCard from "../components/ProductCard";
import HeroBannerSlider from "../components/HeroBannerSlider";
import TestimonialsSection from "../components/TestimonialsSection";
import PageMeta from "../components/PageMeta";

const WHY = [
  { icon: ShieldCheck, title: "Quality First", text: "Multi-stage QC on every batch — raw material to final packaging. Tournament-grade consistency, guaranteed." },
  { icon: Factory, title: "In-House Manufacturing", text: "Our own facility runs thermoforming, cold-press, and rotomolding lines. No middlemen, full control." },
  { icon: Palette, title: "Full Customization", text: "Colors, graphics, grips, packaging, and branding. If you can spec it, we can build it." },
  { icon: BadgeDollarSign, title: "Competitive Pricing", text: "Factory-direct rates with transparent tiered pricing that scales with your volume." },
  { icon: Globe2, title: "Worldwide Shipping", text: "Air and sea freight to 40+ countries with complete export documentation handled for you." },
];

const STEPS = [
  { n: "01", t: "Select Product", d: "Browse the catalog or bring your own spec." },
  { n: "02", t: "Customize", d: "Colors, sizes, logo, packaging — your call." },
  { n: "03", t: "Send Inquiry", d: "Tell us quantity and requirements." },
  { n: "04", t: "Get Quote", d: "Detailed quotation within 24 hours." },
  { n: "05", t: "Production & Delivery", d: "Sample, approval, production, shipment." },
];

const INQUIRY_TYPES = [
  { id: "product", icon: Package, label: "A Product", desc: "Order straight from the catalog" },
  { id: "custom", icon: PencilRuler, label: "Custom Product", desc: "Built exactly to your spec" },
  { id: "bulk", icon: Boxes, label: "Bulk Order", desc: "Wholesale volumes, tiered pricing" },
  { id: "private-label", icon: Tag, label: "Private Label", desc: "Your brand, our factory" },
];

const CAT_CACHE_KEY = "pbg_home_categories";
const FEAT_CACHE_KEY = "pbg_home_featured";

const getCachedJson = (key) => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (_) {}
  return [];
};

const Home = () => {
  const [featured, setFeatured] = useState(() => getCachedJson(FEAT_CACHE_KEY));
  const [categories, setCategories] = useState(() => getCachedJson(CAT_CACHE_KEY));
  const [loading, setLoading] = useState(() => getCachedJson(CAT_CACHE_KEY).length === 0);
  const site = useSettings();

  useEffect(() => {
    api.get("/products?featured=true")
      .then((r) => {
        if (Array.isArray(r.data)) {
          const slice = r.data.slice(0, 4);
          setFeatured(slice);
          try { localStorage.setItem(FEAT_CACHE_KEY, JSON.stringify(slice)); } catch (_) {}
        }
      })
      .catch(() => {});

    api.get("/categories")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setCategories(r.data);
          try { localStorage.setItem(CAT_CACHE_KEY, JSON.stringify(r.data)); } catch (_) {}
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isParent = (c) => !c.parent_id || c.parent_id === 0 || c.parent_id === "0" || c.parent_id === "null";
  const parentCategories = categories.filter(isParent);
  const displayCategories = parentCategories.length > 0 ? parentCategories : categories;

  return (
    <div data-testid="home-page">
      <PageMeta
        title="Pickleballgearz — Premium Pickleball OEM/ODM"
        description="Premium OEM/ODM and private-label pickleball paddles, tournament balls, nets, and court equipment. Factory-direct from Sialkot, Pakistan."
      />

      {/* HERO SLIDER */}
      <HeroBannerSlider banners={site.banners} />

      <EditorialMarquee />

      {/* 01 WHY CHOOSE US */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
        <SectionHeading number="01" title="Why Pickleballgearz" sub="The factory behind the brands you already know. Precision, scale, and reliability — without the markup chain." />
        <div className="grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.06}>
              <div className="group h-full bg-white p-8 transition-colors duration-300 hover:bg-brand-ink" data-testid={`why-card-${i}`}>
                <w.icon size={26} className="text-brand-red" />
                <h3 className="mt-6 font-display text-2xl font-medium tracking-tight group-hover:text-white">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-500 group-hover:text-white/60">{w.text}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <Link to="/about" className="flex h-full flex-col justify-between bg-brand-red p-8 text-white transition-colors hover:bg-brand-reddark" data-testid="why-cta-card">
              <p className="font-display text-2xl font-medium tracking-tight">See how we manufacture</p>
              <ArrowUpRight size={28} />
            </Link>
          </Reveal>
        </div>

        {Array.isArray(site.counters) && site.counters.length > 0 && (
          <div className="mt-12 grid grid-cols-2 gap-px border border-black/10 bg-black/10 sm:grid-cols-3 lg:grid-cols-5" data-testid="home-counters-strip">
            {site.counters.map((c, i) => (
              <div key={i} className="bg-white p-6 text-center transition-colors hover:bg-brand-smoke">
                <p className="font-display text-3xl font-semibold text-brand-ink">
                  {c.value}<span className="text-brand-red">{c.suffix || "+"}</span>
                </p>
                <p className="mono-caption mt-1.5 text-neutral-500">{c.label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 02 FEATURED PRODUCTS */}
      <section className="bg-brand-smoke py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading number="02" title="Featured Products" className="!mb-0" />
            <Link to="/products" className="btn-outline mb-2" data-testid="view-all-products">
              View All Products <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* 03 CATEGORIES */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
        <SectionHeading number="03" title="Product Categories" sub="Certified production lines. One tournament-grade quality standard." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {displayCategories.length > 0 ? (
            displayCategories.map((c, i) => {
              const formattedName = formatCategoryName(c.name);
              const rawImg = c.image || c.img || c.image_path || "";
              return (
                <Reveal key={c.id || c.name || i} delay={i * 0.06}>
                  <Link
                    to={`/products?category=${encodeURIComponent(formattedName)}`}
                    data-testid={`category-tile-${formattedName.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group relative block h-64 overflow-hidden border border-black/10 bg-brand-smoke"
                  >
                    {rawImg ? (
                      <img
                        src={pimg(rawImg, 600, 700)}
                        alt={formattedName}
                        loading="lazy"
                        className="h-full w-full object-cover grayscale-[25%] transition duration-700 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    ) : (
                      <div className="h-full w-full bg-brand-ink/90 flex items-center justify-center p-4">
                        <span className="font-display text-lg font-medium text-white/40 text-center">{formattedName}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-5">
                      <div>
                        <span className="block font-display text-lg font-medium text-white">{formattedName}</span>
                        {c.count !== undefined && c.count > 0 && (
                          <span className="font-mono text-[10px] uppercase tracking-wider text-white/60">
                            {c.count} {c.count === 1 ? 'Product' : 'Products'}
                          </span>
                        )}
                      </div>
                      <ArrowUpRight size={18} className="text-white transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                  </Link>
                </Reveal>
              );
            })
          ) : (
            [...Array(5)].map((_, i) => (
              <div key={i} className="h-64 animate-pulse border border-black/10 bg-neutral-200" />
            ))
          )}
        </div>
      </section>

      {/* 04 B2B */}
      <section className="relative overflow-hidden bg-brand-ink py-24 text-white md:py-32">
        <div className="blueprint-grid-dark pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10">
          <SectionHeading dark number="04" title="Built for Business" sub="Wholesale, private label, and custom manufacturing programs for serious buyers." />
          <div className="grid gap-px border border-white/15 bg-white/15 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Wholesale", "Tiered volume pricing on catalog products."],
              ["Bulk Orders", "Container-scale production with dedicated lines."],
              ["Private Label", "Your branding on our proven products."],
              ["Custom Manufacturing", "Full OEM/ODM development from scratch."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="h-full bg-brand-ink p-8 transition-colors hover:bg-white/5">
                  <p className="font-mono text-xs text-brand-red">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-2xl font-medium tracking-tight">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/50">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <Link to="/inquiry?type=bulk" className="btn-primary" data-testid="b2b-request-quote">
              Request a Quote <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 05 HOW IT WORKS */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
        <SectionHeading number="05" title="How It Works" sub="From inquiry to delivery in five clear steps." />
        <div className="grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06}>
              <div className="h-full bg-white p-7" data-testid={`how-step-${i}`}>
                <p className="font-mono text-3xl font-medium text-brand-red">{s.n}</p>
                <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{s.t}</h3>
                <p className="mt-2 text-sm text-neutral-500">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 06 MOQ */}
      <section className="border-y border-black/10 bg-brand-smoke">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 py-24 md:py-32 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <p className="mono-caption text-neutral-500">Minimum Order Quantity</p>
            <p className="mt-4 font-display text-[9rem] font-semibold leading-none tracking-tighter md:text-[13rem]">
              25<span className="text-brand-red">+</span>
            </p>
            <p className="mono-caption mt-2 text-neutral-500">Units — starting MOQ across product lines</p>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-display text-3xl font-medium tracking-tight md:text-4xl">Low MOQs. Serious quality.</h3>
            <p className="mt-5 max-w-md leading-relaxed text-neutral-500">
              We keep minimums accessible so growing brands can start smart — from 25-unit net
              systems to 100-piece paddle runs. Every product page shows its MOQ upfront, and
              our team can discuss flexible terms for first orders.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/moq" className="btn-dark" data-testid="home-moq-info">MOQ Information</Link>
              <Link to="/inquiry" className="btn-outline" data-testid="home-moq-ask">Ask About MOQ</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VERIFIED REVIEWS */}
      <TestimonialsSection />

      {/* 07 INQUIRY CTA */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
        <SectionHeading number="07" title="What are you looking for?" sub="Start an inquiry in under two minutes. Our export team responds within 24 hours." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {INQUIRY_TYPES.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.06}>
              <Link
                to={`/inquiry?type=${t.id}`}
                data-testid={`inquiry-type-${t.id}`}
                className="group flex h-full flex-col border border-black/10 p-8 transition-colors hover:border-brand-ink hover:bg-brand-ink"
              >
                <t.icon size={26} className="text-brand-red" />
                <h3 className="mt-8 font-display text-2xl font-medium tracking-tight group-hover:text-white">{t.label}</h3>
                <p className="mt-2 text-sm text-neutral-500 group-hover:text-white/60">{t.desc}</p>
                <ArrowUpRight size={18} className="mt-auto pt-8 text-neutral-400 transition-all group-hover:translate-x-1 group-hover:text-brand-red" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
